import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, Trash2, Plus, Minus } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => JSON.parse(localStorage.getItem("bc_cart") || "[]"));
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { localStorage.setItem("bc_cart", JSON.stringify(items)); }, [items]);

  const add = useCallback((p) => {
    setItems((prev) => {
      const ex = prev.find((i) => i.product_id === p.id);
      if (ex) return prev.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { product_id: p.id, name: p.name, price: p.price, image: p.image, qty: 1 }];
    });
    setOpen(true);
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) => qty <= 0 ? prev.filter((i) => i.product_id !== id) : prev.map((i) => i.product_id === id ? { ...i, qty } : i));
  }, []);
  const remove = useCallback((id) => setItems((prev) => prev.filter((i) => i.product_id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);

  const checkout = useCallback(async () => {
    if (!user) { setOpen(false); navigate("/login?next=/shop"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setLoading(false);
      alert("Checkout failed. Please try again.");
    }
  }, [user, items, navigate]);

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, count, subtotal, open, setOpen, checkout, loading }}>
      {children}
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)} className="fixed inset-0 bg-black/50 z-[60]" data-testid="cart-overlay" />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-[#F6F5F0] z-[61] flex flex-col shadow-2xl"
              data-testid="cart-drawer"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-[#D1CFC8]">
                <h3 className="font-display font-bold text-xl text-[#1A1C19] flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Your Cart</h3>
                <button onClick={() => setOpen(false)} data-testid="cart-close"><X className="text-[#4A4D48]" /></button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {items.length === 0 && <p className="text-[#4A4D48] text-center mt-16">Your cart is empty.</p>}
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-4 items-center" data-testid={`cart-item-${i.product_id}`}>
                    <div className="w-16 h-16 rounded-lg bg-[#ECE4D6] overflow-hidden shrink-0">
                      {i.image && <img src={i.image} alt={i.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1A1C19] text-sm truncate">{i.name}</p>
                      <p className="text-[#4A4D48] text-sm">${i.price.toFixed(2)}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <button onClick={() => setQty(i.product_id, i.qty - 1)} className="w-6 h-6 rounded bg-[#ECE4D6] flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <span className="text-sm w-6 text-center" data-testid={`cart-qty-${i.product_id}`}>{i.qty}</span>
                        <button onClick={() => setQty(i.product_id, i.qty + 1)} className="w-6 h-6 rounded bg-[#ECE4D6] flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                    </div>
                    <button onClick={() => remove(i.product_id)} className="text-[#4A4D48] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#D1CFC8] px-6 py-5">
                <div className="flex justify-between font-display font-bold text-lg text-[#1A1C19] mb-4">
                  <span>Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <button
                  onClick={checkout}
                  disabled={items.length === 0 || loading}
                  data-testid="cart-checkout"
                  className="w-full py-3.5 rounded-full bg-[#1F5A34] text-[#F6F5F0] font-semibold hover:bg-[#163F25] transition-colors disabled:opacity-50"
                >
                  {loading ? "Redirecting…" : user ? "Checkout" : "Login to Checkout"}
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
